/**
 * Custom Variant Picker Interactions
 * - Color name updates on hover
 * - Product image changes on color hover
 * - Price updates in button
 * - Size selection with underline
 */

(function() {
  'use strict';

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initColorNameDisplay();
    initColorSwatchHover();
    initVariantChange();
    initSizeSelection();
  }

  /**
   * Get color name from a radio input by reading its label's sr-only span
   */
  function getColorNameFromInput(input) {
    if (!input) return '';
    const label = document.querySelector('label[for="' + input.id + '"] .sr-only');
    return label ? label.textContent.trim() : '';
  }

  /**
   * Initialize color name display — sets initial name and updates on change
   */
  function initColorNameDisplay() {
    const variantPicker = document.querySelector('.variant-picker');
    if (!variantPicker) return;

    const colorOption = variantPicker.querySelector('.variant-picker__option--color');
    if (!colorOption) return;

    const legend = colorOption.querySelector('.selected-color-name');
    if (!legend) return;

    // Set initial color name from checked input's label
    const checkedInput = colorOption.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      const name = getColorNameFromInput(checkedInput);
      if (name) legend.textContent = name;
    }

    // Update on every color input change
    const colorInputs = colorOption.querySelectorAll('input[type="radio"]');
    colorInputs.forEach(input => {
      input.addEventListener('change', () => {
        const name = getColorNameFromInput(input);
        if (name) legend.textContent = name;
      });
    });
  }

  /**
   * Initialize color swatch hover effects
   */
  function initColorSwatchHover() {
    const variantPicker = document.querySelector('.variant-picker');
    if (!variantPicker) return;

    const colorOption = variantPicker.querySelector('.variant-picker__option--color');
    if (!colorOption) return;

    const colorNameDisplay = colorOption.querySelector('.selected-color-name');
    if (!colorNameDisplay) return;

    const swatchLabels = colorOption.querySelectorAll('label.color-swatch');

    swatchLabels.forEach(label => {
      const inputId = label.getAttribute('for');
      const input = document.getElementById(inputId);
      if (!input) return;

      const colorName = label.querySelector('.sr-only');

      // Hover: show hovered color name
      label.addEventListener('mouseenter', () => {
        if (colorName) {
          colorNameDisplay.textContent = colorName.textContent.trim();
        }
      });

      // Mouse leave: restore to currently selected color name
      label.addEventListener('mouseleave', () => {
        const checkedInput = colorOption.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          const name = getColorNameFromInput(checkedInput);
          if (name) colorNameDisplay.textContent = name;
        }
      });
    });
  }

  /**
   * Get product data from the page
   */
  function getProductData() {
    const productScript = document.querySelector('script[type="application/json"][data-product]');
    if (!productScript) return null;

    try {
      return JSON.parse(productScript.textContent);
    } catch (e) {
      console.error('Error parsing product data:', e);
      return null;
    }
  }

  /**
   * Update product image in gallery
   */
  function updateProductImage(mediaId) {
    const gallery = document.querySelector('.product-gallery');
    if (!gallery) return;

    const targetMedia = gallery.querySelector(`[data-media-id="${mediaId}"]`);
    if (!targetMedia) return;

    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (carousel && typeof carousel.slideTo === 'function') {
      const mediaIndex = Array.from(gallery.querySelectorAll('[data-media-id]')).indexOf(targetMedia);
      carousel.slideTo(mediaIndex);
    } else {
      const allMedia = gallery.querySelectorAll('.product-gallery__media');
      allMedia.forEach(media => {
        media.style.display = 'none';
      });

      const targetMediaElement = targetMedia.closest('.product-gallery__media');
      if (targetMediaElement) {
        targetMediaElement.style.display = 'block';
      }
    }
  }

  /**
   * Initialize variant change handling (price update)
   */
  function initVariantChange() {
    const variantPicker = document.querySelector('.variant-picker');
    if (!variantPicker) return;

    document.addEventListener('variant:change', (event) => {
      const variant = event.detail.variant;
      if (!variant) return;
      updateButtonPrice(variant.price);
    });
  }

  /**
   * Update button price display
   */
  function updateButtonPrice(price) {
    const priceElement = document.querySelector('[data-product-price]');
    if (!priceElement) return;

    const formattedPrice = formatMoney(price);
    priceElement.textContent = formattedPrice;
  }

  /**
   * Basic money formatting
   */
  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    const value = formatWithDelimiters(cents, 2);
    return formatString.replace(placeholderRegex, value);
  }

  /**
   * Initialize size selection styling
   */
  function initSizeSelection() {
    const variantPicker = document.querySelector('.variant-picker');
    if (!variantPicker) return;

    const sizeOption = variantPicker.querySelector('.variant-picker__option--size, .variant-picker__option[data-option-name*="size"]');
    if (!sizeOption) return;

    const sizeInputs = sizeOption.querySelectorAll('input[type="radio"]');

    sizeInputs.forEach(input => {
      input.addEventListener('change', () => {
        sizeInputs.forEach(otherInput => {
          const label = otherInput.nextElementSibling;
          if (label) label.classList.remove('selected');
        });

        const label = input.nextElementSibling;
        if (label) label.classList.add('selected');
      });
    });
  }

})();